import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionService } from '../services/subscription';
import { 
  UserSubscription, 
  UsageDetails, 
  BannerType 
} from '../types/subscription';
import { safeAdd, toSafeNumber } from '../utils/number';

/**
 * Unified subscription state that combines subscription data, usage details,
 * and UI state management into a single source of truth.
 * 
 * This eliminates race conditions and data duplication between contexts.
 */
interface UnifiedSubscriptionState {
  // Core subscription data
  subscription: UserSubscription | null;
  usageDetails: UsageDetails | null;
  
  // Loading states
  isLoading: boolean;
  isUsageLoading: boolean;
  error: string | null;
  
  // Derived states (memoized)
  isTrialUser: boolean;
  hasActiveSubscription: boolean;
  isApproachingLimits: boolean;
  hasReachedLimits: boolean;

  // Subscription status states
  isPastDue: boolean;
  isCanceled: boolean;
  isCanceledAtPeriodEnd: boolean;
  hasSubscriptionIssue: boolean;  // true if past_due, canceled, or other problem
  isAlertBannerVisible: boolean;  // true if alert banner is showing (not dismissed)
  dismissAlertBanner: () => void;
  
  // Banner state
  banner: {
    show: boolean;
    type: BannerType | null;
    dismissed: string[];
  };
  
  // Modal state
  modal: {
    isOpen: boolean;
    feature: string;
    reason: 'credits_exceeded' | 'syncs_exceeded' | 'stores_exceeded' | 'trial_expired' | 'no_subscription';
  };
  
  // Actions
  refreshSubscriptionData: () => Promise<void>;
  checkFeatureAccess: (feature: string, requiredCredits?: number) => Promise<boolean>;
  dismissBanner: () => void;
  openSubscriptionModal: (feature: string, reason: UnifiedSubscriptionState['modal']['reason']) => void;
  closeSubscriptionModal: () => void;
}

const UnifiedSubscriptionContext = createContext<UnifiedSubscriptionState | undefined>(undefined);

export const useUnifiedSubscription = (): UnifiedSubscriptionState => {
  const context = useContext(UnifiedSubscriptionContext);
  if (!context) {
    throw new Error('useUnifiedSubscription must be used within UnifiedSubscriptionProvider');
  }
  return context;
};

interface UnifiedSubscriptionProviderProps {
  children: ReactNode;
}

/**
 * Unified Subscription Provider that manages all subscription-related state
 * in a single place, eliminating race conditions and data duplication.
 */
export const UnifiedSubscriptionProvider: React.FC<UnifiedSubscriptionProviderProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Core state
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [usageDetails, setUsageDetails] = useState<UsageDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUsageLoading, setIsUsageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Banner state
  const [bannerDismissed, setBannerDismissed] = useState<string[]>([]);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerType, setBannerType] = useState<BannerType | null>(null);
  
  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    feature: '',
    reason: 'no_subscription' as UnifiedSubscriptionState['modal']['reason']
  });
  
  // Load dismissed banners from localStorage on mount
  useEffect(() => {
    const dismissed = localStorage.getItem('dismissed-banners');
    if (dismissed) {
      try {
        const parsedDismissed = JSON.parse(dismissed);
        // Clean up any old format 'limit-warning' entries
        const cleanedDismissed = parsedDismissed.filter((key: string) => key !== 'limit-warning');
        setBannerDismissed(cleanedDismissed);
        // Update localStorage if we cleaned any entries
        if (cleanedDismissed.length !== parsedDismissed.length) {
          localStorage.setItem('dismissed-banners', JSON.stringify(cleanedDismissed));
        }
      } catch (error) {
        console.error('Failed to parse dismissed banners:', error);
      }
    }
  }, []);
  
  // Memoized derived states
  const isTrialUser = useMemo(() => {
    return Boolean(
      subscription && 
      (subscription.plan_name === 'Trial' || 
       subscription.status === 'trialing' ||
       subscription.status === 'trial')
    );
  }, [subscription]);
  
  const hasActiveSubscription = useMemo(() => {
    return Boolean(
      subscription &&
      (subscription.status === 'active' || subscription.status === 'trialing')
    );
  }, [subscription]);

  // Subscription issue states
  const isPastDue = useMemo(() => {
    return Boolean(subscription && subscription.status === 'past_due');
  }, [subscription]);

  const isCanceled = useMemo(() => {
    return Boolean(subscription && subscription.status === 'canceled');
  }, [subscription]);

  const isCanceledAtPeriodEnd = useMemo(() => {
    // Only true if cancel_at_period_end is set AND subscription is still active (not fully canceled)
    return Boolean(
      subscription &&
      subscription.cancel_at_period_end &&
      subscription.status !== 'canceled'
    );
  }, [subscription]);

  const hasSubscriptionIssue = useMemo(() => {
    return isPastDue || isCanceled || isCanceledAtPeriodEnd;
  }, [isPastDue, isCanceled, isCanceledAtPeriodEnd]);

  // Track dismissed alert banners (persisted per session)
  const [dismissedAlertBanners, setDismissedAlertBanners] = useState<Set<string>>(() => {
    const stored = sessionStorage.getItem('dismissedSubscriptionAlerts');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  const getAlertStatus = useCallback((): string | null => {
    if (isPastDue) return 'past_due';
    if (isCanceled) return 'canceled';
    if (isCanceledAtPeriodEnd) return 'canceling';
    return null;
  }, [isPastDue, isCanceled, isCanceledAtPeriodEnd]);

  const isAlertBannerVisible = useMemo(() => {
    const alertStatus = getAlertStatus();
    if (!alertStatus) return false;
    // Only 'canceling' is dismissible
    if (alertStatus === 'canceling' && dismissedAlertBanners.has(alertStatus)) return false;
    return true;
  }, [getAlertStatus, dismissedAlertBanners]);

  const dismissAlertBanner = useCallback(() => {
    const alertStatus = getAlertStatus();
    if (alertStatus && alertStatus === 'canceling') {
      setDismissedAlertBanners(prev => {
        const newSet = new Set(prev).add(alertStatus);
        sessionStorage.setItem('dismissedSubscriptionAlerts', JSON.stringify(Array.from(newSet)));
        return newSet;
      });
    }
  }, [getAlertStatus]);
  
  const isApproachingLimits = useMemo(() => {
    if (!usageDetails || !usageDetails.credits) return false;
    
    const creditsPercentage = usageDetails.credits.percentage || 0;
    
    // Only consider credit usage for approaching limits
    return creditsPercentage >= 80;
  }, [usageDetails]);
  
  const hasReachedLimits = useMemo(() => {
    if (!usageDetails || !usageDetails.credits) return false;
    
    const creditsPercentage = usageDetails.credits.percentage || 0;
    
    // Only consider credit usage for reached limits
    return creditsPercentage >= 100;
  }, [usageDetails]);
  
  /**
   * Determine banner type based on current state
   * This is a pure function with no side effects
   */
  const determineBannerType = useCallback((): BannerType | null => {
    // No banner if no user or still loading
    if (!user || authLoading) {
      return null;
    }
    
    // If user exists but no subscription, show trial expired
    if (!subscription) {
      return 'trial_expired';
    }
    
    // No banner without usage data (except for trial users who should see welcome banner)
    if (!usageDetails || !usageDetails.credits) {
      // Still show trial banner for new trial users
      if (isTrialUser && !bannerDismissed.includes('trial-welcome')) {
        return 'trial';
      }
      return null;
    }
    
    const creditsPercentage = usageDetails.credits.percentage || 0;
    const creditsUsed = usageDetails.credits.current || 0;
    
    // Trial user logic
    if (isTrialUser) {
      // Trial expired when credits are exhausted
      if (creditsPercentage >= 100) {
        return 'trial_expired';
      }
      // Trial welcome banner for active trial users
      if (!bannerDismissed.includes('trial-welcome')) {
        return 'trial';
      }
    }
    
    // For paid users only (not trial users)
    if (!isTrialUser) {
      // Limit reached - when credits hit 100%
      if (creditsPercentage >= 100 && !bannerDismissed.includes('limit-reached')) {
        return 'limit_reached';
      }
      
      // Warning at 80% - only if actually using credits
      if (creditsPercentage >= 80 && creditsPercentage < 100 && creditsUsed > 0) {
        const bannerKey = `warning-${subscription.plan_name}-${Math.floor(creditsPercentage / 10) * 10}`;
        if (!bannerDismissed.includes(bannerKey)) {
          return 'limit_warning';
        }
      }
      
      // Upgrade recommendation for Starter users with significant usage
      // Starter plan has 10,000 credits limit
      if (subscription.plan_name === 'Starter' && creditsUsed > 5000 && 
          !bannerDismissed.includes('upgrade-recommendation')) {
        return 'upgrade_recommendation';
      }
    }
    
    return null;
  }, [user, authLoading, subscription, usageDetails, isTrialUser, bannerDismissed]);
  
  /**
   * Load subscription and usage data in parallel
   * This eliminates the multiple API call issue
   */
  const refreshSubscriptionData = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setSubscription(null);
      setUsageDetails(null);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch subscription data
      const currentSubscription = await subscriptionService.getCurrentSubscription();
      setSubscription(currentSubscription);
      
      // Only load usage if we have a subscription
      if (currentSubscription) {
        setIsUsageLoading(true);
        try {
          const usage = await subscriptionService.getUsageDetails();
          setUsageDetails(usage);
        } catch (err) {
          console.error('Failed to load usage details:', err);
          // Don't fail completely if usage fails to load
          // Clear usage details if we can't load them
          setUsageDetails(null);
        } finally {
          setIsUsageLoading(false);
        }
      } else {
        // Clear usage details if no subscription
        setUsageDetails(null);
      }
    } catch (err) {
      console.error('Failed to load subscription data:', err);
      setError('Failed to load subscription information');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);
  
  /**
   * Check if user has access to a feature based on subscription limits
   */
  const checkFeatureAccess = useCallback(async (
    feature: string, 
    requiredCredits: number = 1
  ): Promise<boolean> => {
    if (!user) {
      openSubscriptionModal(feature, 'no_subscription');
      return false;
    }
    
    if (!subscription) {
      openSubscriptionModal(feature, 'trial_expired');
      return false;
    }
    
    // Refresh usage data to get latest
    if (!usageDetails) {
      await refreshSubscriptionData();
    }
    
    if (!usageDetails) {
      return false;
    }
    
    // Check credits with safe arithmetic
    if (usageDetails.credits) {
      const currentCredits = toSafeNumber(usageDetails.credits.current);
      const creditLimit = toSafeNumber(usageDetails.credits.limit);
      const requiredCreditsValid = toSafeNumber(requiredCredits);
      
      if (safeAdd(currentCredits, requiredCreditsValid) > creditLimit) {
        openSubscriptionModal(feature, 'credits_exceeded');
        return false;
      }
    }
    
    // Check syncs for sync-related features
    if (feature.includes('sync') && usageDetails.syncs && usageDetails.syncs.current_month >= usageDetails.syncs.limit) {
      openSubscriptionModal(feature, 'syncs_exceeded');
      return false;
    }
    
    // Check stores for store creation
    if (feature.includes('store') && usageDetails.stores && usageDetails.stores.current >= usageDetails.stores.limit) {
      openSubscriptionModal(feature, 'stores_exceeded');
      return false;
    }
    
    return true;
  }, [user, subscription, usageDetails, refreshSubscriptionData]);
  
  /**
   * Banner management functions
   */
  const dismissBanner = useCallback(() => {
    setShowBanner(false);
    
    // Remember dismissal - use credits percentage for consistency
    const bannerKey = bannerType === 'trial' 
      ? 'trial-welcome'
      : bannerType === 'limit_warning' && subscription && usageDetails && usageDetails.credits
      ? `warning-${subscription.plan_name}-${Math.floor((usageDetails.credits.percentage || 0) / 10) * 10}`
      : bannerType === 'upgrade_recommendation'
      ? 'upgrade-recommendation'
      : bannerType === 'limit_reached'
      ? 'limit-reached'
      : '';
    
    if (bannerKey) {
      const newDismissed = [...bannerDismissed, bannerKey];
      setBannerDismissed(newDismissed);
      localStorage.setItem('dismissed-banners', JSON.stringify(newDismissed));
    }
  }, [bannerType, subscription, usageDetails, bannerDismissed]);
  
  /**
   * Modal management functions
   */
  const openSubscriptionModal = useCallback((
    feature: string,
    reason: UnifiedSubscriptionState['modal']['reason']
  ) => {
    setModalState({
      isOpen: true,
      feature,
      reason
    });
  }, []);
  
  const closeSubscriptionModal = useCallback(() => {
    setModalState({
      isOpen: false,
      feature: '',
      reason: 'no_subscription'
    });
  }, []);
  
  
  /**
   * Load data when user is authenticated
   */
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      refreshSubscriptionData();
    }
  }, [isAuthenticated, authLoading, refreshSubscriptionData]);
  
  /**
   * Update banner visibility when relevant state changes
   */
  useEffect(() => {
    if (!isLoading && !authLoading) {
      const newBannerType = determineBannerType();
      setBannerType(newBannerType);
      setShowBanner(!!newBannerType);
    }
  }, [isLoading, authLoading, determineBannerType]);
  
  const value: UnifiedSubscriptionState = {
    // Core data
    subscription,
    usageDetails,
    
    // Loading states
    isLoading: isLoading || authLoading,
    isUsageLoading,
    error,
    
    // Derived states
    isTrialUser,
    hasActiveSubscription,
    isApproachingLimits,
    hasReachedLimits,

    // Subscription status states
    isPastDue,
    isCanceled,
    isCanceledAtPeriodEnd,
    hasSubscriptionIssue,
    isAlertBannerVisible,
    dismissAlertBanner,
    
    // Banner state
    banner: {
      show: showBanner,
      type: bannerType,
      dismissed: bannerDismissed
    },
    
    // Modal state
    modal: modalState,
    
    // Actions
    refreshSubscriptionData,
    checkFeatureAccess,
    dismissBanner,
    openSubscriptionModal,
    closeSubscriptionModal
  };
  
  return (
    <UnifiedSubscriptionContext.Provider value={value}>
      {children}
    </UnifiedSubscriptionContext.Provider>
  );
};

// Export the provider with the old name for backward compatibility
export const SubscriptionProvider = UnifiedSubscriptionProvider;

// Export the old hook interface for backward compatibility
export const useSubscription = () => {
  const unified = useUnifiedSubscription();
  
  // Map unified state to old interface for backward compatibility
  return {
    // Direct mappings
    usageDetails: unified.usageDetails,
    isTrialUser: unified.isTrialUser,
    checkFeatureAccess: unified.checkFeatureAccess,
    openSubscriptionModal: unified.openSubscriptionModal,
    closeSubscriptionModal: unified.closeSubscriptionModal,
    dismissBanner: unified.dismissBanner,
    refreshUsage: unified.refreshSubscriptionData,
    
    // Mapped properties
    showSubscriptionBanner: unified.banner.show,
    bannerType: unified.banner.type,
    subscriptionModalState: {
      isOpen: unified.modal.isOpen,
      feature: unified.modal.feature,
      reason: unified.modal.reason
    }
  };
};